import axiosInstance from './axiosInstance';

import type { Response } from './axiosInstance';

export type verifyPasswordStrengthParams = {
  password: string;
}

export const verifyPasswordStrength = async (params: verifyPasswordStrengthParams): Promise<Response<undefined> | undefined> => {
  try {
    const response = await axiosInstance.post<Response<undefined>>('/auth/verifyPasswordStrength', params, {
      headers: {
        'Content-Type': 'application/json',
        'Skip-Auth': true
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to verify password strength', e);
    return undefined;
  }
};


export type loginParams = {
  email: string;
  password: string;
}

export type authResponse = {
  token: string;
  username: string;
}

export const login = async (params: loginParams): Promise<Response<authResponse> | undefined> => {
  try {
    const response = await axiosInstance.post<Response<authResponse>>('/auth/login', params, {
      headers: {
        'Content-Type': 'application/json',
        'Skip-Auth': true
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to login', e);
    return undefined
  }
};

export const refreshToken = async (): Promise<Response<authResponse> | undefined> => {
  try {
    const response = await axiosInstance.get<Response<authResponse>>('/auth/refreshToken', {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to refresh token', e);
    return undefined
  }
};

export type Region = {
  name: string;
  'alpha-2': string;
  'country-code': string;
};

export const getRegions = async (): Promise<Response<Region[]> | undefined> => {
  try {
    const response = await axiosInstance.get<Response<Region[]>>('/regions', {
      headers: {
        'Skip-Auth': true,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (e) {
    return undefined;
  }
};

export type RegisterDTO = {
  nickName: string;
  email: string;
  password: string;
  confirm: string;
  first_name: string;
  verification_code: string;
  region: string;
  last_name: string;
  title: string;
  phone_number: string;
  institute: string;
  is_subscribed?: boolean;
};

export const register = async (params: RegisterDTO): Promise<Response<authResponse> | undefined> => {
  try {
    const response = await axiosInstance.post<Response<authResponse>>('/auth/register', params, {
      headers: {
        'Content-Type': 'application/json',
        'Skip-Auth': true
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to register', e);
    return undefined;
  }
};

export const valid = async (route: string): Promise<Response<undefined> | undefined> => {
  try {
    const response = await axiosInstance.get(`/auth/valid?route=${route}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (e) {
    return undefined;
  }
};

type userIsReadyResponse = {
  isReady: boolean;
}

export const userIsReady = async (): Promise<Response<userIsReadyResponse> | undefined> => {
  try {
    const response = await axiosInstance.get(`/auth/userIsReady`, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (e) {
    return undefined;
  }
};

export const userIsProtomiaPro = async (): Promise<Response<boolean> | undefined> => {
  try {
    const response = await axiosInstance.get(`/auth/isProUser`, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (e) {
    return undefined;
  }
};

type ResetPasswordParams = {
  email: string;
  verificationCode: string;
  password: string;
  confirmPassword: string;
}

export const resetPassword = async (params: ResetPasswordParams): Promise<Response<boolean> | undefined> => {
  try {
    const response = await axiosInstance.post<Response<boolean>>('/auth/resetPassword', params, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to reset password', e);
    return undefined;
  }
}

export const sendRegisterVerificationCode = async (email: string): Promise<Response<boolean> | undefined> => {
  try {
    const response = await axiosInstance.get<Response<boolean>>('/auth/sendVerificationCode', {
      params: {
        email,
        purpose: 'registration'
      },
      headers: {
        'Content-Type': 'application/json',
        'Skip-Auth': true
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to send verification', e);
    return undefined;
  }
}

export const sendVerificationCode = async (email: string, purpose: string): Promise<Response<boolean> | undefined> => {
  try {
    const response = await axiosInstance.get<Response<boolean>>('/auth/sendVerificationCode', {
      params: {
        email,
        purpose
      },
      headers: {
        'Content-Type': 'application/json',
        'Skip-Auth': true
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to send verification', e);
    return undefined;
  }
}

type FeedbackParams = {
  subject: string;
  message: string;
}

export const sendFeedback = async (params: FeedbackParams): Promise<Response<boolean> | undefined> => {
  try {
    const response = await axiosInstance.post<Response<boolean>>('/auth/feedback', params, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to send feedback', e);
    return undefined;
  }
}

type DeregisterParams = {
  email: string;
  verificationCode: string;
}

export const deregisterUser = async (params: DeregisterParams): Promise<Response<boolean> | undefined> => {
  try {
    const response = await axiosInstance.post<Response<boolean>>('/auth/deregister', params, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to send feedback', e);
    return undefined;
  }
}
export type SelfServiceCapabilities = {
  /** Whether users can create their own account. */
  registration: boolean;
  /** Whether users can reset their own password by emailed code. */
  password_reset: boolean;
  /** Whether users can delete their own account. */
  account_deletion: boolean;
  /**
   * Whether the "how do I get verified" guide is meaningful. False when accounts
   * are pre-provisioned and access is granted by an administrator.
   */
  access_verification: boolean;
};

export type AuthCapabilities = {
  self_service: SelfServiceCapabilities;
  login_accepts_username: boolean;
};

/**
 * Which self-service account flows this deployment supports.
 *
 * Unauthenticated on purpose: the login page has to decide whether to render the
 * sign-up and password-reset links before anybody is signed in.
 */
export const getAuthCapabilities = async (): Promise<Response<AuthCapabilities> | undefined> => {
  try {
    const response = await axiosInstance.get<Response<AuthCapabilities>>('/auth/capabilities', {
      headers: {
        'Content-Type': 'application/json',
        'Skip-Auth': true
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to get auth capabilities', e);
    return undefined;
  }
};
